import { inject, Inject, Injectable, InjectionToken } from '@angular/core';
import { WINDOW } from '@Core/services/proprietary/browser-storage/browser-storage.tokens';
import { generateUUID } from 'src/ep-utils';

export const CRYPTO = new InjectionToken<Crypto>('Abstraction over window.crypto object', {
    factory: () => inject(WINDOW).crypto
});

@Injectable({ providedIn: 'root' })
export class CryptoService {
    private sessionSecret: string;

    constructor(@Inject(CRYPTO) private readonly crypto: Crypto) {
        this.generateSessionSecret();
    }

    public generateSessionSecret() {
        this.sessionSecret = generateUUID();
    }

    public async hashKey(keyPartial: string) {
        const encode = new TextEncoder().encode(keyPartial);
        const hashBuffer = await this.crypto.subtle.digest('SHA-256', encode);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        return hashHex;
    }

    public async encrypt(data: any): Promise<string> {
        const encryptedData = await this.encryptDataWithPassword(data, this.sessionSecret);
        return encryptedData;
    }

    public async decrypt(encryptedData: string): Promise<string> {
        if (this.isEncrypted(encryptedData)) {
            const decryptedData = await this.decryptDataWithPassword(encryptedData, this.sessionSecret);
            return decryptedData;
        } else {
            return Promise.resolve(encryptedData);
        }
    }

    private isEncrypted(value: any) {
        // this actually checks if the value is a base64 string since data is converted to base64 after being encrypted
        // the purpose of this check is to help handle the transition when some values in local storage are encrypted
        // and others are not
        const notBase64 = /[^A-Z0-9+\/=]/i;
        if (typeof value !== 'string') {
            return false;
        }

        const len = value.length;
        if (!len || len % 4 !== 0 || notBase64.test(value)) {
            return false;
        }

        const firstPaddingChar = value.indexOf('=');
        return (
            firstPaddingChar === -1 ||
            firstPaddingChar === len - 1 ||
            (firstPaddingChar === len - 2 && value[len - 1] === '=')
        );
    }

    private async getPasswordKey(password: string): Promise<CryptoKey> {
        return this.crypto.subtle.importKey('raw', this.encode(password), 'PBKDF2', false, ['deriveKey']);
    }

    private async deriveKeyPBKDF2(passwordKey, salt, keyUsage): Promise<CryptoKey> {
        return this.crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: salt,
                iterations: 250000,
                hash: 'SHA-256'
            },
            passwordKey,
            {
                name: 'AES-GCM',
                length: 256
            },
            false,
            keyUsage
        );
    }

    private async encryptDataWithPassword(secretData: any, password: string): Promise<string> {
        try {
            const salt = this.crypto.getRandomValues(new Uint8Array(16));
            const iv = this.crypto.getRandomValues(new Uint8Array(12));
            const passwordKey = await this.getPasswordKey(password);
            const aesKey = await this.deriveKeyPBKDF2(passwordKey, salt, ['encrypt']);
            const encryptedContent = await this.crypto.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv: iv
                },
                aesKey,
                this.encode(secretData)
            );

            const encryptedContentArr = new Uint8Array(encryptedContent);
            let buff = new Uint8Array(salt.byteLength + iv.byteLength + encryptedContentArr.byteLength);
            buff.set(salt, 0);
            buff.set(iv, salt.byteLength);
            buff.set(encryptedContentArr, salt.byteLength + iv.byteLength);
            const base64Buff = this.convertBinaryToBase64String(buff);
            return base64Buff;
        } catch (e) {
            console.log(`Cache Encryption Error: ${e}`);
            return '';
        }
    }

    private async decryptDataWithPassword(encryptedData: string, password: string): Promise<string> {
        try {
            const encryptedDataBuff = this.convertBase64ToBinaryBuffer(encryptedData);
            const salt = encryptedDataBuff.slice(0, 16);
            const iv = encryptedDataBuff.slice(16, 16 + 12);
            const data = encryptedDataBuff.slice(16 + 12);
            const passwordKey = await this.getPasswordKey(password);
            const aesKey = await this.deriveKeyPBKDF2(passwordKey, salt, ['decrypt']);
            const decryptedContent = await this.crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: iv
                },
                aesKey,
                data
            );
            return this.decode(decryptedContent);
        } catch (e) {
            console.log(`Cache Decryption Error: ${e}`);
            return null; //Needs to return null so that cached value is ignored.
        }
    }

    private encode(data: any): Uint8Array<ArrayBuffer> {
        const encoder = new TextEncoder();
        let dataString = '';
        if (typeof data === 'string') {
            dataString = data;
        } else {
            dataString = JSON.stringify(data);
        }
        return encoder.encode(dataString);
    }

    private decode(bytestream: BufferSource): string {
        const decoder = new TextDecoder();
        return decoder.decode(bytestream);
    }

    private convertBinaryToBase64String(buffer): string {
        var binary = '';
        var bytes = new Uint8Array(buffer);
        var len = bytes.byteLength;
        for (var i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    private convertBase64ToBinaryBuffer(base64: string): ArrayBuffer {
        const string = window.atob(base64);
        const buffer = new ArrayBuffer(string.length);
        const bufferView = new Uint8Array(buffer);

        for (let i = 0; i < string.length; i++) {
            bufferView[i] = string.charCodeAt(i);
        }

        return buffer;
    }
}
